import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Card, Alert, Spinner } from '../components/ui';
import { FileBrowser, SharedLinks, ShareDialog, type ShareTarget } from './RecordingFiles';
import { RecordingPlayer, TranscriptMark, fmt, madeBy, offsets, prettyStamp, sessionName, type Preset, type Session } from './RecordingPlayer';

/**
 * Recordings across every machine, encoder and flow.
 *
 * The thing that made a recording can usually browse its own, but this is
 * where anything that must outlive it happens: joining segments for download,
 * cutting clips, sharing. A multiviewer is disposable and usually terminated
 * well before anyone wants a clip from it.
 *
 * The list is here; the player is RecordingPlayer.tsx.
 */
export function Recordings() {
  const { can } = useAuth();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [retentionDays, setRetentionDays] = useState(60);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<Session | null>(null);
  const [filter, setFilter] = useState('');
  const urlCache = useRef(new Map<string, string>());
  // The list and player, the bucket as files (administrators), or the links made.
  const [view, setView] = useState<'list' | 'files' | 'shares'>('list');
  const [presets, setPresets] = useState<Preset[]>([]);
  const [ai, setAi] = useState(false);
  const [share, setShare] = useState<ShareTarget | null>(null);
  const openedFromLink = useRef(false);

  // Which recordings the checkboxes have selected, as prefixes: that is what
  // identifies a recording to the server, and it survives a reload of the list.
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<'selected' | 'all' | null>(null);

  const open = useCallback((session: Session) => {
    setSelected(session);
    // On a phone the player sits below the list: bring it into view.
    if (window.innerWidth < 1024) requestAnimationFrame(() => document.getElementById('recording-player')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, []);

  const load = useCallback(async (): Promise<Session[]> => {
    try {
      const data = await api.get<{ sessions: Session[]; retentionDays: number; compress?: Preset[]; ai?: boolean }>('/recordings');
      setSessions(data.sessions);
      setPresets(data.compress ?? []);
      setAi(!!data.ai);
      // ?open=<id>/<session>: straight to one recording, from an encoder's or flow's page.
      const want = new URLSearchParams(window.location.search).get('open');
      if (want && !openedFromLink.current) {
        const s = data.sessions.find((x) => `${x.instanceId}/${x.session}` === want);
        if (s) { openedFromLink.current = true; open(s); }
      }
      setRetentionDays(data.retentionDays);
      setError(null);
      return data.sessions;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load recordings.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => { void load(); }, [load]);

  // A recording still running grows, and a transcript being made moves on:
  // keep the list current while either is happening.
  useEffect(() => {
    if (!sessions.some((s) => s.recording || s.transcript?.state === 'working')) return;
    const t = setInterval(() => void load(), 20_000);
    return () => clearInterval(t);
  }, [sessions, load]);

  // The open recording's row is what the player reads too (its transcript state).
  useEffect(() => {
    if (!selected) return;
    const again = sessions.find((x) => x.prefix === selected.prefix);
    if (again && again !== selected) setSelected(again);
  }, [sessions, selected]);

  const signed = useCallback(async (key: string) => {
    const cached = urlCache.current.get(key);
    if (cached) return cached;
    const { url } = await api.get<{ url: string }>(`/recordings/url?key=${encodeURIComponent(key)}`);
    urlCache.current.set(key, url);
    return url;
  }, []);

  /** Which sprite, and where in it, for a moment in a recording. */
  const spriteAt = useCallback(
    async (s: Session, t: number) => {
      // Sheets the hub made for the whole recording (an encoder's or flow's):
      // cells count from the start, `grid` of them a sheet.
      if (s.sprites) {
        const { interval, grid: [cols, rows], sheets } = s.sprites;
        const idx = Math.floor(Math.max(0, t) / interval);
        const per = cols * rows;
        const sheet = Math.floor(idx / per) + 1;
        if (sheet > sheets) return null;
        const cell = idx % per;
        const col = cell % cols;
        const row = Math.floor(cell / cols);
        try {
          return {
            url: await signed(`${s.prefix}/sprites/sheet-${String(sheet).padStart(4, '0')}.jpg`),
            size: `${cols * 100}% ${rows * 100}%`,
            pos: `${cols > 1 ? (col / (cols - 1)) * 100 : 0}% ${rows > 1 ? (row / (rows - 1)) * 100 : 0}%`,
          };
        } catch { return null; }
      }
      const seg = [...offsets(s)].reverse().find((x) => t >= x.start);
      if (!seg?.sprite) {
        // No sheet for this moment: the still the hub made, if there is one.
        if (t === 0 && s.poster) { try { return { url: await signed(s.poster), size: 'cover', pos: 'center' }; } catch { return null; } }
        return null;
      }
      const idx = Math.floor((t - seg.start) / (seg.spriteInterval || 2));
      const [cols, rows] = seg.spriteGrid || [10, 10];
      if (idx >= cols * rows) return null;
      // Percentages, so one cell fills whatever box it is put in.
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      try {
        return {
          url: await signed(`${s.prefix}/sprites/${seg.tag}.jpg`),
          size: `${cols * 100}% ${rows * 100}%`,
          pos: `${cols > 1 ? (col / (cols - 1)) * 100 : 0}% ${rows > 1 ? (row / (rows - 1)) * 100 : 0}%`,
        };
      } catch {
        return null;
      }
    },
    [signed],
  );

  // A still from the start of each recording, so the list reads as pictures.
  // Hovering across a still scrubs through the same sprite sheet.
  const [posters, setPosters] = useState<Record<string, { url: string; pos: string; size: string }>>({});
  const spriteLoaded = useRef(new Set<string>());
  const lastPreview = useRef(0);

  // Pointing backgroundImage at a sheet not loaded yet paints an empty box
  // until it arrives; waiting for it means the previous frame simply stays up.
  const preload = useCallback(
    (url: string) =>
      spriteLoaded.current.has(url)
        ? Promise.resolve(true)
        : new Promise<boolean>((resolve) => {
            const img = new Image();
            img.onload = () => { spriteLoaded.current.add(url); resolve(true); };
            img.onerror = () => resolve(false);
            img.src = url;
          }),
    [],
  );

  const showFrame = useCallback(
    async (key: string, cell: { url: string; pos: string; size: string } | null) => {
      if (!cell || !(await preload(cell.url))) return;
      setPosters((p) => (p[key]?.url === cell.url && p[key]?.pos === cell.pos ? p : { ...p, [key]: cell }));
    },
    [preload],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const s of sessions) {
        const key = `${s.instanceId}-${s.session}`;
        if (posters[key]) continue;
        const cell = await spriteAt(s, 0);
        if (cancelled) return;
        await showFrame(key, cell);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, spriteAt]);

  /**
   * Delete whole recordings, permanently. One request per recording, so a
   * failure part way through still leaves the rest deleted and the list shows
   * what actually survived.
   */
  async function removeMany(targets: Session[]) {
    setBusy('delete');
    setConfirming(null);
    const failed: string[] = [];
    for (const s of targets) {
      try {
        await api.delete(`/recordings/${s.instanceId}/${s.session}`);
      } catch {
        failed.push(`${sessionName(s)} ${prettyStamp(s.session)}`);
      }
    }
    if (selected && targets.some((t) => t.prefix === selected.prefix)) setSelected(null);
    setChecked(new Set());
    setError(failed.length ? `Could not delete: ${failed.join(', ')}.` : null);
    setNotice(failed.length ? `Deleted ${targets.length - failed.length} of ${targets.length}.` : `Deleted ${targets.length} recording${targets.length === 1 ? '' : 's'}.`);
    await load();
    setBusy(null);
  }

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sessions;
    // "#tag" finds by tag alone; anything else searches names, sources, dates and tags.
    if (q.startsWith('#')) return sessions.filter((s) => (s.tags ?? []).some((t) => t.includes(q.slice(1))));
    return sessions.filter((s) => `${sessionName(s)} ${madeBy(s)} ${s.source} ${prettyStamp(s.session)} ${(s.tags ?? []).map((t) => `#${t}`).join(' ')}`.toLowerCase().includes(q));
  }, [sessions, filter]);

  if (loading) return <Spinner />;

  const checkedSessions = sessions.filter((s) => checked.has(s.prefix));
  const canDelete = can('recording:delete');
  const shareChecked = () => setShare({
    kind: 'set', target: '', targets: checkedSessions.map((s) => s.prefix),
    name: checkedSessions.length === 1 ? `${sessionName(checkedSessions[0])} ${prettyStamp(checkedSessions[0].session).slice(0, 10)}` : `${checkedSessions.length} recordings`,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Recordings</h1>
          <p className="text-sm text-ink-muted">
            Kept for {retentionDays} days, then deleted permanently. Download or export anything worth keeping.
          </p>
        </div>
        <div className="flex gap-1 border-b border-grid text-sm">
          {([['list', 'Recordings'], ...(can('role:manage') ? [['files', 'Files']] : []), ['shares', 'Shared links']] as [typeof view, string][]).map(([v, label]) => (
            <button
              key={v}
              className={`-mb-px border-b-2 px-3 py-2 ${view === v ? 'border-series-1 font-medium text-ink' : 'border-transparent text-ink-muted hover:text-ink'}`}
              onClick={() => setView(v)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && <Alert tone="critical">{error}</Alert>}
      {notice && <Alert tone="info">{notice}</Alert>}

      {view === 'files' && (
        <FileBrowser onOpenSession={(instanceId, session) => {
          const s = sessions.find((x) => x.instanceId === instanceId && x.session === session);
          if (s) { setView('list'); open(s); }
        }} />
      )}
      {view === 'shares' && <SharedLinks />}
      <ShareDialog target={share} onClose={() => setShare(null)} />

      <div className={`grid gap-4 lg:grid-cols-[340px_1fr] ${view === 'list' ? '' : 'hidden'}`}>
        <Card
          title={`${sessions.length} recording${sessions.length === 1 ? '' : 's'}`}
          action={sessions.length > 5 ? (
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Find…"
              className="w-28 rounded border border-grid bg-plane px-2 py-1 text-xs"
            />
          ) : undefined}
        >
          {sessions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-b border-grid px-3 py-2">
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-ink-muted">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-series-1"
                  checked={checked.size === sessions.length && sessions.length > 0}
                  ref={(el) => { if (el) el.indeterminate = checked.size > 0 && checked.size < sessions.length; }}
                  onChange={(e) => setChecked(e.target.checked ? new Set(sessions.map((s) => s.prefix)) : new Set())}
                />
                {checked.size ? `${checked.size} selected` : 'Select all'}
              </label>
              <div className="flex-1" />
              {checked.size > 0 && (
                <Button variant="ghost" title="One link for all the selected recordings" onClick={shareChecked}>Share selected…</Button>
              )}
              {canDelete && checked.size > 0 && (
                <Button variant="ghost" disabled={busy !== null} onClick={() => setConfirming('selected')}>
                  {busy === 'delete' ? 'Deleting…' : 'Delete selected'}
                </Button>
              )}
              {canDelete && checked.size === 0 && (
                <Button variant="ghost" disabled={busy !== null} onClick={() => setConfirming('all')}>Delete all</Button>
              )}
            </div>
          )}

          {/* Deletion is permanent, so it is confirmed in place, naming what goes. */}
          {confirming && (
            <div className="border-b border-grid bg-plane px-3 py-3">
              <Alert tone="critical">
                Permanently delete{' '}
                {confirming === 'all'
                  ? `all ${sessions.length} recording${sessions.length === 1 ? '' : 's'}`
                  : `${checkedSessions.length} selected recording${checkedSessions.length === 1 ? '' : 's'}`}
                ? There is no backup and this cannot be undone.
              </Alert>
              <div className="mt-2 flex gap-2">
                <Button variant="danger" onClick={() => void removeMany(confirming === 'all' ? sessions : checkedSessions)}>Delete permanently</Button>
                <Button variant="ghost" onClick={() => setConfirming(null)}>Cancel</Button>
              </div>
            </div>
          )}

          <div className="max-h-[40vh] divide-y divide-grid overflow-auto lg:max-h-[75vh]">
            {sessions.length === 0 && (
              <div className="px-4 py-8 text-center text-xs text-ink-muted">
                Nothing recorded yet. Recording is a launch option on multiviewer instances, and a card on every encoder and flow.
              </div>
            )}
            {sessions.length > 0 && shown.length === 0 && (
              <div className="px-4 py-8 text-center text-xs text-ink-muted">Nothing matches "{filter}".</div>
            )}
            {shown.map((s) => {
              const days = s.expiresInDays;
              const rowKey = `${s.instanceId}-${s.session}`;
              const isOpen = selected?.session === s.session && selected?.instanceId === s.instanceId;
              const poster = posters[rowKey];
              return (
                <div key={rowKey} className={`relative ${isOpen ? 'bg-raised' : ''}`}>
                  {(
                    // Outside the button: a checkbox nested in a button cannot
                    // be clicked without also opening the recording.
                    <label className="absolute right-2 top-2 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded bg-black/60" title="Select, to share or delete together">
                      <input
                        type="checkbox"
                        className="cursor-pointer accent-series-1"
                        checked={checked.has(s.prefix)}
                        onChange={(e) => setChecked((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(s.prefix); else next.delete(s.prefix);
                          return next;
                        })}
                      />
                    </label>
                  )}
                  {/* items-start: the still keeps its 16:9 shape instead of stretching to a row made taller by a title and tags. */}
                  <button onClick={() => open(s)} className={`flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-raised ${isOpen ? 'border-l-2 border-series-1' : 'border-l-2 border-transparent'}`}>
                    {/* Hover across the still to scrub the whole recording:
                        the same sprite sheets the player uses. */}
                    <div
                      className="aspect-video w-28 shrink-0 overflow-hidden rounded bg-plane"
                      onMouseMove={(e) => {
                        const now = performance.now();
                        if (now - lastPreview.current < 60) return;
                        lastPreview.current = now;
                        const r = e.currentTarget.getBoundingClientRect();
                        const frac = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
                        void (async () => showFrame(rowKey, await spriteAt(s, frac * s.seconds)))();
                      }}
                      onMouseLeave={() => { void (async () => showFrame(rowKey, await spriteAt(s, 0)))(); }}
                    >
                      {poster ? (
                        <div className="h-full w-full" style={{ backgroundImage: `url("${poster.url}")`, backgroundPosition: poster.pos, backgroundSize: poster.size, backgroundRepeat: 'no-repeat' }} />
                      ) : (
                        <div className="flex h-full items-center justify-center text-[10px] text-ink-muted">{fmt(s.seconds)}</div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 pr-6">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <span className="truncate">{sessionName(s)}</span>
                        <TranscriptMark t={s.transcript} />
                        {s.recording && <span className="shrink-0 text-xs" style={{ color: 'var(--color-critical)' }}>● recording</span>}
                      </div>
                      <div className="text-xs text-ink-muted">{s.title ? `${madeBy(s)} · ` : ''}{prettyStamp(s.session)}</div>
                      <div className="text-xs text-ink-muted">
                        {fmt(s.seconds)}
                        {days != null && days <= 10 && (
                          <span className={`ml-2 ${days <= 3 ? 'text-bad' : 'text-warning'}`}>· deleted in {days} day{days === 1 ? '' : 's'}</span>
                        )}
                      </div>
                      {!!s.tags?.length && (
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {s.tags.map((t) => <span key={t} className="rounded-full bg-raised px-1.5 text-[11px] text-ink-secondary">#{t}</span>)}
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </Card>

        {selected ? (
          <RecordingPlayer
            key={selected.prefix}
            session={selected}
            presets={presets}
            canDelete={canDelete}
            ai={ai}
            onShare={() => setShare({
              kind: 'folder', target: `${selected.prefix}/`,
              name: `${sessionName(selected)} ${prettyStamp(selected.session).slice(0, 10)}`,
            })}
            onShareTag={(tag) => setShare({ kind: 'tag', target: tag, name: `#${tag}` })}
            onDelete={() => { setChecked(new Set([selected.prefix])); setConfirming('selected'); }}
            onChanged={() => void load().then((list) => { const again = list.find((x) => x.prefix === selected.prefix); if (again) setSelected(again); })}
          />
        ) : (
          <Card title="Player">
            <div className="p-4">
              <div className="flex aspect-video w-full items-center justify-center rounded bg-plane text-sm text-ink-muted">
                Pick a recording to play it.
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
